/**
 * Ingredient Aliases
 * 
 * Maps canonical ingredient names to common variants.
 * Used for flexible recipe matching - user can type "chicken breast"
 * and it matches recipes needing "chicken".
 * 
 * Rules:
 * - Canonical name is the key (what recipes use)
 * - Aliases are variants users might have in pantry
 * - Distinct items are separate keys (rotisserie chicken ≠ chicken)
 */

const INGREDIENT_ALIASES = {
  // === PROTEINS (RAW) ===
  'chicken': [
    'chicken breast', 'chicken thigh', 'chicken leg', 'chicken drumstick',
    'chicken wing', 'raw chicken', 'fresh chicken', 'boneless chicken'
  ],
  'ground beef': [
    'beef mince', 'minced beef', 'ground chuck', 'hamburger meat'
  ],
  'beef': [
    'steak', 'beef steak', 'sirloin', 'ribeye', 'flank steak',
    'stewing beef', 'beef chunks', 'braising beef'
  ],
  'pork': [
    'pork chop', 'pork loin', 'pork tenderloin', 'pork steak'
  ],
  'ground pork': [
    'pork mince', 'minced pork'
  ],
  'ground lamb': [
    'lamb mince', 'minced lamb'
  ],
  'lamb': [
    'lamb chop', 'lamb leg', 'lamb shoulder', 'lamb steak'
  ],
  'ground turkey': [
    'turkey mince', 'minced turkey'
  ],
  'turkey': [
    'turkey breast', 'turkey thigh', 'fresh turkey'
  ],
  'salmon': [
    'salmon fillet', 'fresh salmon', 'salmon steak'
  ],
  'white fish': [
    'cod', 'haddock', 'tilapia', 'sea bass', 'halibut', 'pollock',
    'fish fillet', 'white fish fillet'
  ],
  'shrimp': [
    'prawns', 'king prawns', 'tiger prawns', 'raw shrimp', 'raw prawns'
  ],
  'tofu': [
    'firm tofu', 'extra firm tofu', 'silken tofu', 'bean curd'
  ],
  'pork chop': [
    'pork chops', 'bone-in pork chop', 'boneless pork chop'
  ],
  'lamb shank': [
    'lamb shanks'
  ],
  'turkey breast': [
    'turkey breast joint', 'boneless turkey breast'
  ],

  // === PROTEINS (READY TO EAT) ===
  'rotisserie chicken': [
    'cooked chicken', 'leftover chicken', 'shredded chicken', 'pre-cooked chicken'
  ],
  'deli turkey': [
    'turkey slices', 'sliced turkey', 'turkey breast slices', 'lunch meat turkey'
  ],
  'deli chicken': [
    'chicken slices', 'sliced chicken'
  ],
  'ham': [
    'sliced ham', 'deli ham', 'cooked ham', 'honey ham', 'smoked ham'
  ],
  'bacon': [
    'streaky bacon', 'back bacon', 'smoked bacon', 'pancetta'
  ],
  'sausage': [
    'pork sausage', 'italian sausage', 'breakfast sausage', 'sausage links'
  ],
  'deli meat': [
    'lunch meat', 'cold cuts', 'sandwich meat'
  ],
  'tuna': [
    'canned tuna', 'tinned tuna', 'tuna in water', 'tuna in oil', 'tuna chunks'
  ],
  'smoked salmon': [
    'lox', 'nova', 'gravlax', 'cold smoked salmon'
  ],
  'roast beef': [
    'sliced roast beef', 'deli roast beef', 'beef slices'
  ],

  // === DAIRY ===
  'cheese': [
    'cheddar', 'cheddar cheese', 'american cheese', 'jack cheese',
    'swiss', 'swiss cheese', 'gruyere', 'gouda', 'provolone',
    'shredded cheese', 'sliced cheese', 'block cheese'
  ],
  'mozzarella': [
    'fresh mozzarella', 'mozzarella cheese', 'shredded mozzarella'
  ],
  'parmesan': [
    'parmesan cheese', 'parmigiano', 'grated parmesan'
  ],
  'feta': [
    'feta cheese', 'crumbled feta'
  ],
  'cream cheese': [
    'philadelphia', 'soft cheese'
  ],
  'cottage cheese': [
    'small curd cottage cheese', 'large curd cottage cheese'
  ],
  'yogurt': [
    'plain yogurt', 'natural yogurt', 'greek yogurt', 'yoghurt'
  ],
  'milk': [
    'whole milk', 'semi-skimmed milk', '2% milk', 'skim milk', 'skimmed milk'
  ],
  'butter': [
    'unsalted butter', 'salted butter'
  ],
  'egg': [
    'eggs', 'large egg', 'large eggs', 'chicken egg', 'free range egg'
  ],
  'sour cream': [
    'soured cream', 'creme fraiche'
  ],

  // === GRAINS & CARBS ===
  'bread': [
    'white bread', 'wholemeal bread', 'whole wheat bread', 'sliced bread',
    'sandwich bread', 'loaf', 'sourdough'
  ],
  'tortilla': [
    'flour tortilla', 'wheat tortilla', 'soft tortilla', 'wrap', 'wraps'
  ],
  'rice': [
    'white rice', 'basmati', 'basmati rice', 'jasmine rice', 'long grain rice',
    'brown rice', 'cooked rice', 'leftover rice'
  ],
  'pasta': [
    'spaghetti', 'penne', 'fusilli', 'rigatoni', 'linguine', 'fettuccine',
    'macaroni', 'farfalle', 'dried pasta'
  ],
  'noodles': [
    'egg noodles', 'rice noodles', 'udon', 'lo mein noodles', 'ramen noodles'
  ],
  'ramen': [
    'instant ramen', 'ramen noodles', 'instant noodles'
  ],
  'oats': [
    'rolled oats', 'porridge oats', 'old fashioned oats', 'oatmeal'
  ],
  'flour': [
    'plain flour', 'all-purpose flour', 'all purpose flour', 'ap flour', 'white flour'
  ],
  'breadcrumbs': [
    'bread crumbs', 'panko', 'panko breadcrumbs'
  ],
  'crackers': [
    'saltines', 'ritz crackers', 'water crackers', 'cream crackers'
  ],
  'bagel': [
    'bagels', 'plain bagel', 'everything bagel', 'sesame bagel'
  ],
  'pitta': [
    'pita', 'pitta bread', 'pita bread', 'pocket bread'
  ],
  'granola': [
    'muesli', 'granola cereal'
  ],

  // === VEGETABLES ===
  'tomato': [
    'tomatoes', 'cherry tomato', 'cherry tomatoes', 'roma tomato',
    'vine tomato', 'fresh tomato', 'fresh tomatoes'
  ],
  'canned tomato': [
    'tinned tomato', 'tinned tomatoes', 'canned tomatoes', 'chopped tomatoes',
    'diced tomatoes', 'crushed tomatoes', 'tomato tin'
  ],
  'onion': [
    'onions', 'yellow onion', 'white onion', 'brown onion'
  ],
  'garlic': [
    'garlic cloves', 'fresh garlic', 'minced garlic'
  ],
  'potato': [
    'potatoes', 'russet potato', 'baking potato', 'white potato', 'yukon gold'
  ],
  'carrot': [
    'carrots', 'fresh carrot', 'fresh carrots'
  ],
  'celery': [
    'celery stalk', 'celery stalks', 'celery sticks'
  ],
  'peppers': [
    'bell pepper', 'bell peppers', 'capsicum', 'red pepper', 'green pepper',
    'yellow pepper', 'sweet pepper'
  ],
  'cucumber': [
    'cucumbers', 'english cucumber'
  ],
  'lettuce': [
    'iceberg lettuce', 'romaine', 'romaine lettuce', 'salad leaves', 'mixed greens'
  ],
  'spinach': [
    'baby spinach', 'fresh spinach'
  ],
  'mushroom': [
    'mushrooms', 'button mushroom', 'button mushrooms', 'cremini', 'white mushroom'
  ],
  'avocado': [
    'avocados', 'ripe avocado'
  ],
  'corn': [
    'sweet corn', 'sweetcorn', 'corn kernels', 'canned corn', 'tinned corn'
  ],
  'peas': [
    'green peas', 'frozen peas', 'garden peas'
  ],
  'beans': [
    'canned beans', 'tinned beans', 'black beans', 'kidney beans', 'pinto beans',
    'mixed beans', 'baked beans'
  ],
  'lentils': [
    'red lentils', 'green lentils', 'brown lentils', 'puy lentils', 'dried lentils'
  ],
  'chickpeas': [
    'canned chickpeas', 'tinned chickpeas', 'garbanzo beans', 'garbanzo'
  ],
  'green onion': [
    'green onions', 'spring onion', 'spring onions', 'scallion', 'scallions'
  ],

  // === FRUITS ===
  'banana': [
    'bananas', 'ripe banana'
  ],
  'apple': [
    'apples', 'green apple', 'red apple'
  ],
  'berries': [
    'mixed berries', 'fresh berries', 'blueberries', 'strawberries', 
    'raspberries', 'blackberries'
  ],
  'lemon': [
    'lemons', 'fresh lemon'
  ],
  'grapes': [
    'red grapes', 'green grapes'
  ],

  // === SAUCES & CONDIMENTS ===
  'pasta sauce': [
    'marinara', 'marinara sauce', 'tomato sauce jar', 'jar sauce', 
    'spaghetti sauce', 'red sauce'
  ],
  'tomato sauce': [
    'passata', 'tomato puree', 'tomato paste', 'pizza sauce'
  ],
  'soy sauce': [
    'soya sauce', 'light soy sauce', 'dark soy sauce', 'tamari'
  ],
  'salsa': [
    'tomato salsa', 'fresh salsa', 'jar salsa', 'pico de gallo'
  ],
  'mayo': [
    'mayonnaise', 'hellmanns', 'light mayo'
  ],
  'hummus': [
    'houmous', 'humous'
  ],
  'peanut butter': [
    'peanut butter smooth', 'peanut butter crunchy', 'pb'
  ],
  'curry paste': [
    'thai curry paste', 'red curry paste', 'green curry paste', 'yellow curry paste'
  ],
  'taco seasoning': [
    'taco spice', 'taco mix', 'fajita seasoning'
  ],
  'enchilada sauce': [
    'red enchilada sauce', 'green enchilada sauce'
  ],
  'stock': [
    'broth', 'chicken stock', 'chicken broth', 'beef stock', 'beef broth',
    'vegetable stock', 'veggie stock', 'stock cube', 'bouillon'
  ],

  // === OILS & FATS ===
  'oil': [
    'vegetable oil', 'canola oil', 'cooking oil', 'neutral oil'
  ],
  'olive oil': [
    'extra virgin olive oil', 'evoo'
  ],
  'sesame oil': [
    'toasted sesame oil'
  ],

  // === BAKING ===
  'yeast': [
    'active dry yeast', 'instant yeast', 'dried yeast'
  ],
  'honey': [
    'raw honey', 'runny honey'
  ],
  'maple syrup': [
    'pure maple syrup', 'pancake syrup'
  ],

  // === HERBS & SPICES ===
  'herbs': [
    'mixed herbs', 'italian herbs', 'dried herbs', 'fresh herbs'
  ],
  'basil': [
    'fresh basil', 'dried basil'
  ],
  'parsley': [
    'fresh parsley', 'dried parsley', 'flat leaf parsley'
  ],
  'cilantro': [
    'fresh cilantro', 'coriander', 'fresh coriander'
  ],
  'chili flakes': [
    'red pepper flakes', 'crushed red pepper', 'chilli flakes'
  ],
  'ginger': [
    'fresh ginger', 'ginger root', 'minced ginger'
  ],

  // === OTHER ===
  'coconut milk': [
    'canned coconut milk', 'tinned coconut milk', 'coconut cream'
  ],
  'olives': [
    'black olives', 'kalamata olives', 'green olives'
  ],
  'nuts': [
    'mixed nuts', 'almonds', 'walnuts', 'cashews', 'pecans'
  ],
  'chia seeds': [
    'chia'
  ],
  'pickle': [
    'pickles', 'dill pickle', 'gherkin', 'gherkins'
  ]
};

/**
 * Find canonical ingredient name from user input
 * Returns the canonical name if found, otherwise returns normalized input
 */
export function getCanonicalIngredient(input) {
  const normalized = input.toLowerCase().trim();
  
  // Check if input is already a canonical name
  if (INGREDIENT_ALIASES[normalized]) {
    return normalized;
  }
  
  // Search through aliases
  for (const [canonical, aliases] of Object.entries(INGREDIENT_ALIASES)) {
    if (aliases.some(alias => alias === normalized)) {
      return canonical;
    }
  }
  
  // Handle basic plurals - remove trailing 's' and check again
  const singular = normalized.replace(/s$/, '');
  if (INGREDIENT_ALIASES[singular]) {
    return singular;
  }
  for (const [canonical, aliases] of Object.entries(INGREDIENT_ALIASES)) {
    if (aliases.some(alias => alias === singular || alias.replace(/s$/, '') === singular)) {
      return canonical;
    }
  }
  
  // No match found - return normalized input
  return normalized;
}

/**
 * Check if two ingredients match (considering aliases)
 */
export function ingredientsMatch(ingredient1, ingredient2) {
  return getCanonicalIngredient(ingredient1) === getCanonicalIngredient(ingredient2);
}

/**
 * Get all aliases for an ingredient (including canonical name)
 */
export function getIngredientAliases(ingredient) {
  const canonical = getCanonicalIngredient(ingredient);
  const aliases = INGREDIENT_ALIASES[canonical] || [];
  return [canonical, ...aliases];
}

export default INGREDIENT_ALIASES;
